import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import api from '../../services/api';
import { config } from '../../config/config';

// How often the report silently re-fetches in the background.
const AUTO_REFRESH_MS = 60000;

// Local YYYY-MM-DD (no timezone shift), for the date pickers + API params.
const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface CSTRow {
  import_pro: string;
  domestic_pro: string;
  movetype: string;
  equiptype: string;
  return_pro: string;
  return_equipment: string;
  return_chassis: string;
  equipment: string;
  bill_to: string;
  customer_name: string;
  deliver_to: string;
  city_state_zip: string;
  partner_carrier: string;
  delivery_appt: string | null;
  confirmed_delivery: string | null;
  revenue: number | null;
  total_expense: number | null;
  carrier_expense: number | null;
  profit: number | null;
  margin: number | null;
}

// Format a date-only value (YYYY-MM-DD, or the date portion of an ISO
// datetime) as M/D/YY without any timezone shifting.
const formatDate = (dateStr: string | null): string => {
  if (!dateStr || dateStr.startsWith('0000-00-00')) return '-';
  const [y, m, d] = dateStr.slice(0, 10).split('-');
  if (y && m && d) {
    return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
  }
  return dateStr;
};

const formatCurrency = (value: number | null): string => {
  if (value == null) return '-';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// dashboard_cst.margin is stored as a percent value.
const formatPercent = (value: number | null): string => {
  if (value == null) return '-';
  return `${value.toFixed(1)}%`;
};

// Sum a numeric column across the given (filtered) rows for the subtotal footer.
const sumBy = (
  rows: { original: CSTRow }[],
  key: keyof CSTRow
): number => rows.reduce((acc, r) => acc + (Number(r.original[key]) || 0), 0);

function CSTContent() {
  // Any orders-scope registry field can be added as a column here; the backend
  // attaches it to the rows this report already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('cst');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;

  const [data, setData] = useState<CSTRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Delivery-appt date range. Defaults to Mon-Sun of the current week. Future
  // dates are allowed; floored at one year back (backend clamps the same way).
  const { minStr, defaultStart, defaultEnd } = useMemo(() => {
    const min = new Date();
    min.setFullYear(min.getFullYear() - 1);
    const now = new Date();
    const dow = (now.getDay() + 6) % 7; // 0 = Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() - dow);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { minStr: toYMD(min), defaultStart: toYMD(monday), defaultEnd: toYMD(sunday) };
  }, []);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchData = useCallback(async (mode: 'initial' | 'background' = 'background') => {
    if (!isMounted.current) return;
    const initial = mode === 'initial';

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (initial) {
        setError(null);
        setData([]);
        setLoading(true);
      }

      // The saved selection is part of the request; going early just fetches twice.
      if (!fieldsReady) return;
      const response = await api.get('/reporting/cst', {
        params: {
          start_date: startDate,
          end_date: endDate,
          // A timer-driven refresh is the page keeping itself current, not somebody
          // asking for the report, so it is not recorded as a run.
          ...(initial ? {} : { background: true }),
          ...(additionalFieldsKey ? { additional_fields: additionalFieldsKey } : {}),
        },
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
      setError(null);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      if (initial) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
    } finally {
      if (isMounted.current && !controller.signal.aborted && initial) {
        setLoading(false);
      }
    }
  }, [startDate, endDate, fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    fetchData('initial');
  }, [fetchData]);

  useVisibilityAwareInterval(() => {
    fetchData('background');
  }, AUTO_REFRESH_MS);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Clickable PRO cell — opens the pro drawer + marks viewed, with a link to CMSV1.
  const renderProCell = useCallback((pronumber: string) => {
    if (!pronumber) return <span className="text-gray-400">-</span>;
    // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
    // trackViewedColumnId wrapper on the pro columns.
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openOrderDrawer(Number(pronumber));
          }}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        >
          {pronumber}
        </button>
        <a
          href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Open in CMSV1"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
      </div>
    );
  }, [openOrderDrawer]);

  const baseColumns = useMemo<ColumnDef<CSTRow>[]>(() => [
    {
      accessorKey: 'bill_to',
      header: 'Bill To',
      // The bill-to NAME (`b.cname`), where the registry's `billto` its id normalizes
      // onto is the bill-to NUMBER — different data, so no registry title.
      meta: { registryTitle: false },
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
      footer: () => <span className="font-semibold">Totals</span>,
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'movetype',
      header: 'Movetype',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'domestic_pro',
      header: 'Pronumber',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.domestic_pro),
    },
    {
      accessorKey: 'import_pro',
      header: 'Import Pro',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.import_pro),
    },
    {
      accessorKey: 'equipment',
      header: 'Equipment #',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'equiptype',
      header: 'Equipment Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'return_pro',
      header: 'Return Pro',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.return_pro),
    },
    {
      accessorKey: 'return_equipment',
      header: 'Return Equipment #',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'return_chassis',
      header: 'Return Chassis',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'deliver_to',
      header: 'Deliver To',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'city_state_zip',
      header: 'City State, Zip',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'partner_carrier',
      header: 'Partner Carrier',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'delivery_appt',
      header: 'Delivery Appt',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.delivery_appt),
    },
    {
      accessorKey: 'confirmed_delivery',
      header: 'Confirmed Delivery',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.confirmed_delivery),
    },
    {
      accessorKey: 'revenue',
      header: 'Revenue',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.revenue),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'revenue'))}
        </span>
      ),
    },
    {
      accessorKey: 'total_expense',
      header: 'Total Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.total_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'total_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'carrier_expense',
      header: 'Carrier Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.carrier_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'carrier_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'profit',
      header: 'Profit',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.profit),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'profit'))}
        </span>
      ),
    },
    {
      accessorKey: 'margin',
      header: 'Margin',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatPercent(row.original.margin),
      // Blended margin across the filtered rows: total profit / total revenue.
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows;
        const revenue = sumBy(rows, 'revenue');
        return (
          <span className="font-semibold">
            {revenue !== 0 ? formatPercent((sumBy(rows, 'profit') / revenue) * 100) : '-'}
          </span>
        );
      },
    },
  ], [renderProCell]);

  // Rows are report rows, not orders — several can share a pro — so an added order
  // column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<CSTRow>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: CSTRow) => {
    return `${row.domestic_pro}|${row.import_pro}`;
  }, []);

  const infoBanner = (
    <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      Brokerage orders on the CST dashboard — available-good-to-go imports plus non-import moves,
      excluding flatbed/van/reefer equipment — with bill-to, customer, move/equipment type, delivery
      details, partner carrier, and CST financials (revenue, expense, margin). Import PRO shows on
      domestic rows only. Sorted by delivery appointment; the subtotal row reflects the current
      filters. Auto-refreshes every 60 seconds.
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {infoBanner}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {infoBanner}

      <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          From
          <input
            type="date"
            value={startDate}
            min={minStr}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          To
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
          />
        </label>
        <span className="text-xs text-gray-400 dark:text-gray-500">delivery appointment date</span>
      </div>

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId={['domestic_pro', 'import_pro', 'return_pro']}
          loading={loading}
          defaultSorting={[{ id: 'delivery_appt', desc: false }]}
          settingsKey="cst"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="CST"
          fillContainer={true}
          showColumnFooter={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function CST() {
  return (
    <ViewedProsProvider>
      <CSTContent />
    </ViewedProsProvider>
  );
}

export default CST;
